import { FC, Fragment, useEffect, useState } from "react";
import {
  App,
  Button,
  DatePicker,
  Descriptions,
  DescriptionsProps,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  notification,
  Select,
  Spin,
} from "antd";
import {
  QuotationRequestItem,
  QuotationRequestItemStatus,
} from "@/types/quotationRequestItem";
import { useSSR, useTranslation } from "react-i18next";
import dayjs from "dayjs";
import apis, { quotationPurchase, QuotationRequestSubmit } from "@/apis/quotationRequestItem";
import type { QuotationPurchaseRound } from "@/apis/quotationRequestItem";
import useDataDetail from "@/hooks/useDataDetail";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import DetailModal from "../DetailModal";
import SText from "@/styles/SText";
import { PageId } from "@/types/page";
import useRequirementParams from "@/pages/Requirement/hooks/useRequirementParams";
import { RootState } from "@/store";
import { useSelector } from "react-redux";
import { Org } from "@/types/org";
import { RequirementItem } from "@/types/requirement";
import { itemUpdate } from "@/apis/requirement";
import { divided, multi } from "@/utils/calc";
import { numFormmater } from "@/pages/Requirement/util";
import { getSuppLadderPrice } from "@/utils/getSuppLadderPrice";

interface QuotationFormProps {
  formKey: "add" | "detail" | "cooperate" | "";
  detailId: string;
  onSubmit: (detailId?: string, submitData?: QuotationRequestSubmit) => void;
  onReject: (detailId?: string) => void;
  onCancel: () => void;
}

interface FieldType {
  price: number;
  time: number;
  days: number;
  mpq?: number | null;
  remark?: string;
  spareCount: string;
}

const QuotationForm: FC<QuotationFormProps> = ({
  formKey,
  detailId,
  onSubmit,
  onReject,
  onCancel,
}) => {
  const { modal } = App.useApp();
  const { t } = useTranslation();
  const { pageId } = useRequirementParams(true);
  const [purchaseLoading, setPurchaseLoading] = useState<boolean>(false);
  const currentOrg: Org = useSelector((state: RootState) => state.orgReducer.curOrg);
  const [purchaseRound, setPurchaseRound] = useState<QuotationPurchaseRound>(0);

  const {
    loading: deLoading,
    fetchDetail: fetch,
    detail,
  } = useDataDetail<QuotationRequestItem, [string]>({
    api: apis.quotationRequestDetail,
  });

  const { onConfirm } = useFrontDelTip({
    cb: (id: string) => apis.quotationRequestRefuse(id),
    desc: t('supplierLayout.confirmTip'),
    message: t('common.tip'),
  });

  const [form] = Form.useForm<FieldType>();
  const [contractPrice, setContractPrice] = useState<string>('');
  useEffect(() => {
    if (detail) {
      const defaultPrice = getSuppLadderPrice(detail.price, detail.count);
      form.setFieldsValue({
        // 已提交的供应商报价优先；没有报价时沿用合同默认价。
        price: Number(detail.supplierPrice) || Number(defaultPrice) || undefined,
        // 批量报价没有关联供应商价格时，回退使用报价自身的数据。
        days: detail?.price?.days || detail.days || undefined,
        time: detail?.supplierTime || undefined,
        mpq: Number(detail.mpq || detail.price?.mpq || '') || undefined,
        remark: detail.remark,
        spareCount: ["cooperate", "add"].includes(formKey) ? Math.ceil(Number(detail?.price?.spareRate ? multi(divided(detail?.price?.spareRate ?? '', '100'), detail.count) : '')).toString() : detail?.spareCount?.toString()
      })
      setContractPrice(defaultPrice)
    }
  }, [detail, formKey])



  useEffect(() => {
    if (detailId) {
      fetch(detailId).then((res) => {
        if (res) {
          // form.setFieldsValue({
          //   price: Number(res.supplierPrice) || undefined,
          //   time: res.supplierTime
          //     ? dayjs(res.supplierTime).valueOf()
          //     : undefined,
          // });

          if (res.status === QuotationRequestItemStatus.UN_READ) {
            apis.quotationRequestRead(detailId);
          }
        }
      });
    }
  }, [detailId]);

  const descCellStyle = { paddingBottom: 5 };

  const items: DescriptionsProps["items"] = [
    {
      key: "productCode",
      label: t("product.code"),
      children: detail?.requirementItem?.product?.code,
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "productName",
      label: t("product.name"),
      children: detail?.requirementItem?.product?.name,
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "productNum",
      label: t("product.num"),
      children: numFormmater(Number(detail?.count || '')),
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "productModel",
      label: t("product.model"),
      children: detail?.requirementItem?.product?.model,
      span: 24,
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "brand",
      label: t("purchase.brand"),
      children: detail?.price?.brand ?? "",
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "mpn",
      label: t("purchase.mpn"),
      children: detail?.price?.mpn ?? "",
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "dcc",
      label: t("purchase.dcc"),
      children: detail?.price?.dcc ?? "",
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "mrd",
      label: t("requirement.prevDMrd"),
      children: dayjs(detail?.mrd).format("YYYY-MM-DD"),
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "deadline",
      label: t("requirement.deadline"),
      children: dayjs(detail?.deadline).format("YYYY-MM-DD"),
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "price",
      label: t("requirement.rulePrice"),
      children: numFormmater(Number(contractPrice || detail?.price?.price || detail?.supplierPrice || '')),
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "days",
      label: t("requirement.ruleDays"),
      children: detail?.price?.days ?? detail?.days,
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "mpq",
      label: t("requirement.ruleMpq"),
      children: numFormmater(Number(detail?.mpq || '')),
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
    {
      key: "spareRate",
      label: t("purchase.spareRate"),
      children: detail?.price?.spareRate,
      labelStyle: descCellStyle,
      contentStyle: descCellStyle,
    },
  ];



  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (detailId) {
          modal.confirm({
            title: t('requirement.requirementInfo'),
            content: <Fragment>
              <Descriptions items={[
                { label: t("requirement.price"), children: values.price, span: 3 },
                { label: t("requirement.days"), children: values.days, span: 3 },
                { label: t("requirement.ddlTime"), children: dayjs(values.time).format('YYYY-MM-DD'), span: 3 },
                { label: t("requirement.mpq"), children: values.mpq, span: 3 },
                { label: t("purchase.orderDesc"), children: values.remark, span: 3 },
              ]} />
              <Divider orientation="left" />
              <div>{t('requirement.quotationTip')}</div>
            </Fragment>,
            onOk: () => {
              const submitData: QuotationRequestSubmit = {
                supplierPrice: values.price,
                supplierTime: values.time,
                mpq: values.mpq || null,
                days: values.days,
                remark: values.remark,
                spareCount: values.spareCount ?? '0',
                spareRate: multi(divided(values.spareCount ?? '0', detail?.count ?? '0'), '100')
              };

              apis.quotationRequestSubmit(detailId, submitData)
                .then(() => {
                  onSubmit(detailId, submitData);
                  handleCancel();
                }).catch(err => {
                  console.log("confirmFail:::", err);
                  handleCancel();
                })
            },
          })
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleReject = () => {
    if (detailId) {
      onConfirm(detailId).then(() => {
        onReject(detailId);
        handleCancel();
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handlePurchase = () => {
    if (purchaseLoading) return
    setPurchaseLoading(true)
    quotationPurchase([detail?.id || ''], currentOrg?.id || '', purchaseRound)
      .then(async res => {
        if (res && res.length > 0) {
          let updateItem: Array<Promise<RequirementItem>> = []
          res.forEach(r => {
            r.requirementItem && r.purchaseItem && updateItem.push(
              itemUpdate(r.requirementItemId, {
                ...r.requirementItem,
                puredCount: r.purchaseItem.count + '',
              })
            )
          })
          await Promise.allSettled(updateItem)
          notification.success({ placement: 'top', message: t("purchase.addPurSuccessTip") })
        }
        else notification.success({ placement: 'top', message: t("purchase.addNoPurSuccess") })
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setPurchaseLoading(false)
      })
  }

  return (
    <DetailModal
      logBookId={detail?.logBookId || ""}
      modalProps={{
        width: 1200,
        title: <Flex justify="flex-start" align="center" gap={8}>
          <SText text={t("requirement.matInfo")} style={{ width: 66 }} />
          {pageId === PageId.PUR_ORDER_PAYED && !detail?.purchaseItem && (
            <>
              <span>{t("purchase.roundMethod")}</span>
              <Select<QuotationPurchaseRound>
                value={purchaseRound}
                style={{ minWidth: 120 }}
                options={[
                  { label: t("purchase.roundNone"), value: 0 },
                  { label: t("purchase.roundDown"), value: 1 },
                  { label: t("purchase.roundUp"), value: 2 },
                  { label: t("purchase.roundNearest"), value: 3 },
                ]}
                onChange={setPurchaseRound}
              />
              <Button type="primary" loading={purchaseLoading} onClick={handlePurchase}>{t('requirement.goPur')}</Button>
            </>
          )}
        </Flex>,
        onCancel: handleCancel,
        open: !!detailId,
        // loading: deLoading,
        footer: null,
        centered: true
      }}
    >
      {deLoading ? (
        <Flex
          style={{ width: "100%", height: "100%" }}
          justify="center"
          align="center"
        >
          <Spin />
        </Flex>
      ) : (
        <Flex vertical style={{ width: "100%", height: "100%" }}>
          <Descriptions items={items} />
          <Divider style={{ borderBlockStart: "#172B4D" }}>
            {t("requirement.requirementInfo")}
          </Divider>
          <div style={{ flex: 1, overflow: "auto" }}>
            <Form
              key={detailId}
              name="form"
              size="large"
              form={form}
              autoComplete="off"
              colon={false}
              disabled={formKey === "detail"}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                columnGap: 24,
              }}
            >
              <Form.Item<FieldType>
                label={t("requirement.price")}
                name="price"
                validateDebounce={1000}
                rules={[
                  { required: true, message: t("requirement.inputPrice") },
                  () => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.reject();
                      }
                      if (
                        value &&
                        detail?.price?.price &&
                        value <= Number(contractPrice || detail?.price?.price)
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t("requirement.priceTip")));
                    },
                    warningOnly: true,
                  }),
                ]}
              >
                <InputNumber changeOnWheel
                  formatter={val => numFormmater(Number(val || ''))}
                  placeholder={t("requirement.inputPrice")}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item<FieldType>
                label={t("requirement.days")}
                name="days"
              >
                <InputNumber changeOnWheel
                  formatter={val => numFormmater(Number(val || ''))}
                  min={1}
                  step={1}
                  precision={0}
                  placeholder={t("requirement.inputDays")}
                  style={{ width: "100%" }}
                  addonAfter={t("requirement.day")}
                />
              </Form.Item>
              <Form.Item<FieldType>
                label={t("requirement.ddlTime")}
                name="time"
                validateDebounce={1000}
                rules={[
                  { required: true, message: t("requirement.inputDdlTime") },
                  () => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.reject();
                      }
                      const valueDay = dayjs(Number(value ? value : 0));
                      const mrdDay = dayjs(detail?.mrd);
                      if (
                        value &&
                        detail?.mrd &&
                        (valueDay.isSame(mrdDay, "day") ||
                          valueDay.isBefore(mrdDay, "day"))
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(t("requirement.ddlTimeTip"))
                      );
                    },
                    warningOnly: true,
                  }),
                ]}
                getValueProps={(value) => ({
                  value: value && dayjs(Number(value)),
                })}
                normalize={(value) => value && `${dayjs(value).valueOf()}`}
              >
                <DatePicker
                  placeholder={t("requirement.inputDdlTime")}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item<FieldType> label={t("requirement.mpq")} name="mpq">
                <InputNumber changeOnWheel
                  formatter={val => numFormmater(Number(val || ''))}
                  disabled={!detail?.mpq}
                  min={1}
                  step={1}
                  precision={0}
                  placeholder={t("requirement.inputMpq")}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item<FieldType> label={t("purchase.spareCount")} name="spareCount">
                <InputNumber changeOnWheel
                  formatter={val => numFormmater(Number(val || ''))}
                  disabled={!["add", "cooperate"].includes(formKey)}
                  min={1}
                  step={1}
                  precision={0}
                  placeholder={t("purchase.spareCountPl")}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item<FieldType> label={t("purchase.orderDesc")} name="remark" style={{ gridColumn: "1 / -1" }}>
                <Input.TextArea placeholder={t("purchase.plOrderDesc")} autoSize={{ minRows: 2, maxRows: 4 }} maxLength={2000} showCount />
              </Form.Item>
            </Form>

          </div>
          {formKey === "detail" ? null : (
            <Flex gap={10} justify="end" style={{ boxSizing: "border-box", padding: "10px 16px", borderTop: "1px solid #e8e8e8", width: '100%', background: "#ffffff", height: 32 }}>
              <Button type="link" danger onClick={handleReject}>
                {t("requirement.reject")}
              </Button>
              <Button type="primary" onClick={handleSubmit}>
                {t("common.confirm")}
              </Button>
            </Flex>
          )}
        </Flex>
      )}
    </DetailModal>
  );
};

export default QuotationForm;
